import React, { useState, useEffect, useCallback } from 'react'
import '../styles/staking.css'
import '../styles/docs.css'

const HELIUS_RPC = 'https://mainnet.helius-rpc.com/?api-key=f8567a96-fef3-4752-88d4-6338fb67e1b0'

// Addresses
const ADDRESSES = {
  program: 'CSkRjy7gyhWw6izU9jrJUKdwgzCCNM4m8edA4REbjQti',
  poolState: '5mj13zoqBwE61ELA6QjRNjcHDf5bvms62Z48dtvgGXCM',
  poolVault: '28jV6eBH8rsRGeGoaVWcxFvkobfKHH8797nRSLQHTsQt',
  cskrMint: 'GT6LYQN3fnwLm127ULvYVg5xDvmacfUEah6KQQYaK7Jx',
  mintAuthority: 'G2HoxRjhjeX7rHGH6eay866gpxJTLbKsvChpaXbGsfoF',
  skrMint: 'SKRbvo6Gf7GondiT3BbTfuRDPqLWei4j2Qy2NPGZhW3',
  queueEscrow: '6MXj3HzqJwa66Fxwt6QmTmXaQ4HHmy9JmVQ9qTQWeau2',
  treasury: 'BLUErbZsh1JNwzySVxhStopvsk47EaDmoGjXnAbQzgiQ',
  treasurySkrAta: '6Aq428L6M7LBTGbqdq8GbNVXi5YCXFRBKVtgnJiXmoYw',
  deployer: 'PERCHzsgHHDbj2UNCfxJmWtEXjfgV9GcDKj65smBX9x',
  metadata: '95wYUEkbNwAJnBJvGu9eBU1meLyns226nziUVmFw9pKn',
}

function ExplorerLink({ addr, type = 'account' }) {
  const base = type === 'token' ? 'https://orb.helius.dev/token/' : 'https://orb.helius.dev/address/'
  const short = addr.slice(0, 6) + '...' + addr.slice(-4)
  return <a href={`${base}${addr}?cluster=mainnet-beta`} target="_blank" rel="noopener noreferrer"><code>{short}</code></a>
}

function FullAddr({ addr, type = 'account' }) {
  const base = type === 'token' ? 'https://orb.helius.dev/token/' : 'https://orb.helius.dev/address/'
  return <a href={`${base}${addr}?cluster=mainnet-beta`} target="_blank" rel="noopener noreferrer"><code>{addr}</code></a>
}

// Sidebar sections
const NAV = [
  { group: 'Overview', items: [
    { id: 'overview', label: 'What is canary_skr_pool?' },
    { id: 'addresses', label: 'Addresses' },
    { id: 'architecture', label: 'Architecture' },
  ]},
  { group: 'State', items: [
    { id: 'pool-state', label: 'Pool State (190 bytes)' },
    { id: 'exchange-rate', label: 'Exchange Rate' },
  ]},
  { group: 'Instructions', items: [
    { id: 'ix-deposit', label: 'Deposit (tag 1)' },
    { id: 'ix-withdraw', label: 'Withdraw (tag 2)' },
    { id: 'ix-instant', label: 'Instant Unstake (tag 13)' },
    { id: 'ix-admin', label: 'Admin Instructions' },
    { id: 'ix-guardian', label: 'Guardian Staking' },
    { id: 'ix-metadata', label: 'cSKR Metadata (tag 18)' },
  ]},
  { group: 'Integration', items: [
    { id: 'sdk-kotlin', label: 'Kotlin / Android' },
    { id: 'sdk-web', label: 'Web / JavaScript' },
    { id: 'fees', label: 'Fee Structure' },
    { id: 'security', label: 'Security Model' },
  ]},
]

export default function StakingDocs() {
  const [activeSection, setActiveSection] = useState('overview')
  const [liveState, setLiveState] = useState(null)

  // Fetch live pool state for the "live data" badge
  const fetchPoolState = useCallback(async () => {
    try {
      const res = await fetch(HELIUS_RPC, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0', id: 1,
          method: 'getAccountInfo',
          params: [ADDRESSES.poolState, { encoding: 'base64' }],
        }),
      })
      const json = await res.json()
      if (json.result?.value?.data) {
        const raw = Buffer.from(json.result.value.data[0], 'base64')
        const view = new DataView(raw.buffer, raw.byteOffset)
        // 190-byte v6 layout with 8-byte "canpools" discriminator
        const totalCskr = Number(view.getBigUint64(104, true))
        const totalSkr = Number(view.getBigUint64(112, true))
        const depositFee = view.getUint16(136, true)
        const instantFee = view.getUint16(166, true)
        const yieldComm = raw.length >= 182 ? view.getUint16(180, true) : 0
        setLiveState({
          totalSkr, totalCskr, depositFee, instantFee, yieldComm,
          ratio: totalCskr > 0 ? totalSkr / totalCskr : 1.0,
        })
      }
    } catch (e) {
      console.error('Failed to fetch pool state for docs:', e)
    }
  }, [])

  useEffect(() => { fetchPoolState() }, [fetchPoolState])

  // Scroll spy
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
            break
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    )
    NAV.forEach(g => g.items.forEach(item => {
      const el = document.getElementById(item.id)
      if (el) observer.observe(el)
    }))
    return () => observer.disconnect()
  }, [])

  return (
    <div className="docs-page">
      {/* Nav */}
      <nav className="staking-nav">
        <a className="staking-nav-brand" href="/staking">
          <div className="logo">C</div>
          <span>Canary Staking</span>
        </a>
        <div className="staking-nav-links">
          <a href="/staking">Dashboard</a>
          <a href="/staking/docs" className="active">Documentation</a>
          <a href="/rewards">Epoch Rewards</a>
        </div>
        <div className="staking-nav-wallet" />
      </nav>

      <div className="docs-layout">
        {/* Sidebar */}
        <aside className="docs-sidebar">
          {NAV.map(group => (
            <div key={group.group}>
              <h3>{group.group}</h3>
              {group.items.map(item => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={activeSection === item.id ? 'active' : ''}
                  onClick={e => { e.preventDefault(); document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }}
                >
                  {item.label}
                </a>
              ))}
            </div>
          ))}
        </aside>

        {/* Content */}
        <main className="docs-content">
          <h1>canary_skr_pool</h1>
          <p className="subtitle">
            On-chain liquid staking program for SKR → cSKR on Solana Mainnet-Beta
          </p>

          {/* ── OVERVIEW ─────────────────────────────────────────── */}
          <h2 id="overview">What is canary_skr_pool?</h2>
          <p>
            <strong>canary_skr_pool</strong> is a Solana program that implements liquid staking for the SKR token.
            Users deposit SPL SKR tokens into a shared vault and receive <strong>cSKR</strong> (Canary Staked SKR),
            a liquid receipt token whose exchange rate against SKR increases over time as staking rewards
            accrue to the pool.
          </p>
          <p>
            The program is built with <strong>Pinocchio v0.10.2</strong> (zero-copy Solana framework) and
            <strong> pinocchio-token v0.5.0</strong> for SPL token operations. The compiled binary is{' '}
            <strong>113,368 bytes</strong> and deployed via BPFLoaderUpgradeable.
          </p>

          <div className="docs-callout info">
            <strong>Live on Mainnet.</strong> The pool is initialized and accepting deposits.
            {liveState && (
              <> Current ratio: <strong>{liveState.ratio.toFixed(6)}</strong> SKR per cSKR.
              Total staked: <strong>{(liveState.totalSkr / 1e6).toFixed(2)} SKR</strong>. Version 6.</>
            )}
          </div>

          <h3>Key Features</h3>
          <table>
            <thead><tr><th>Feature</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td>Liquid Staking</td><td>Deposit SKR, receive transferable cSKR receipt tokens</td></tr>
              <tr><td>Auto-compounding</td><td>Exchange rate increases as rewards accrue. No manual claiming.</td></tr>
              <tr><td>Instant Unstake</td><td>Skip the cooldown queue for a fee (currently 3%) sent to treasury</td></tr>
              <tr><td>Guardian Staking</td><td>Pool authority can delegate to external validators/guardians</td></tr>
              <tr><td>Metaplex Metadata</td><td>cSKR has on-chain token metadata ("Canary Staked SKR")</td></tr>
              <tr><td>Upgradeable</td><td>BPFLoaderUpgradeable with authority control</td></tr>
            </tbody>
          </table>

          {/* ── ADDRESSES ────────────────────────────────────────── */}
          <h2 id="addresses">Addresses</h2>
          <table>
            <thead><tr><th>Name</th><th>Address</th></tr></thead>
            <tbody>
              <tr><td>Program</td><td><FullAddr addr={ADDRESSES.program} /></td></tr>
              <tr><td>Pool State PDA</td><td><FullAddr addr={ADDRESSES.poolState} /></td></tr>
              <tr><td>Pool Vault (SKR)</td><td><FullAddr addr={ADDRESSES.poolVault} /></td></tr>
              <tr><td>cSKR Mint</td><td><FullAddr addr={ADDRESSES.cskrMint} type="token" /></td></tr>
              <tr><td>Mint Authority PDA</td><td><FullAddr addr={ADDRESSES.mintAuthority} /></td></tr>
              <tr><td>SKR Mint</td><td><FullAddr addr={ADDRESSES.skrMint} type="token" /></td></tr>
              <tr><td>Queue Escrow</td><td><FullAddr addr={ADDRESSES.queueEscrow} /></td></tr>
              <tr><td>Treasury</td><td><FullAddr addr={ADDRESSES.treasury} /></td></tr>
              <tr><td>Treasury SKR ATA</td><td><FullAddr addr={ADDRESSES.treasurySkrAta} /></td></tr>
              <tr><td>Upgrade Authority</td><td><FullAddr addr={ADDRESSES.deployer} /></td></tr>
              <tr><td>Metadata PDA</td><td><FullAddr addr={ADDRESSES.metadata} /></td></tr>
            </tbody>
          </table>

          <h3>PDA Derivation</h3>
          <pre><code>{`// Pool State PDA
seeds = ["pool_state"]
program = CSkRjy7gyhWw6izU9jrJUKdwgzCCNM4m8edA4REbjQti

// Mint Authority PDA
seeds = ["mint_authority"]
program = CSkRjy7gyhWw6izU9jrJUKdwgzCCNM4m8edA4REbjQti

// Queue Escrow PDA
seeds = ["queue_escrow"]
program = CSkRjy7gyhWw6izU9jrJUKdwgzCCNM4m8edA4REbjQti

// Unstake Request PDA (per-user)
seeds = ["unstake_req", user_pubkey]
program = CSkRjy7gyhWw6izU9jrJUKdwgzCCNM4m8edA4REbjQti`}</code></pre>

          {/* ── ARCHITECTURE ─────────────────────────────────────── */}
          <h2 id="architecture">Architecture</h2>
          <pre><code>{`┌─────────────┐     deposit SKR      ┌──────────────────┐
│  User Wallet │──────────────────────▶│  Pool Vault      │
│             │◀──────────────────────│  (SPL Token ATA) │
│             │     receive cSKR      └──────────────────┘
└─────────────┘                                │
       │                                       │
       │ withdraw cSKR                         │ rewards
       │ (burn cSKR → receive SKR)             │ accrue
       ▼                                       ▼
┌─────────────┐                       ┌──────────────────┐
│  cSKR Mint  │                       │  Pool State PDA  │
│  (burn on   │                       │  (190 bytes v6)  │
│   withdraw) │                       │  ratio increases │
└─────────────┘                       └──────────────────┘
                                               │
                            ┌──────────────────┤
                            │                  │
                    ┌───────▼──────┐   ┌───────▼──────┐
                    │  Treasury    │   │ Queue Escrow │
                    │  (fees)      │   │ (instant fee)│
                    └──────────────┘   └──────────────┘`}</code></pre>
          <p>
            The exchange rate is computed as: <code>ratio = total_skr_deposited / total_cskr_supply</code>.
            When rewards are added to the vault (via guardian staking or direct deposits by the authority),
            <code>total_skr_deposited</code> increases while <code>total_cskr_supply</code> stays the same,
            causing the ratio to rise. This means each cSKR becomes worth more SKR over time.
          </p>

          {/* ── POOL STATE ───────────────────────────────────────── */}
          <h2 id="pool-state">Pool State (190 bytes)</h2>
          <p>
            The pool state account is a PDA derived from <code>["pool_state"]</code>.
            It uses a fixed 190-byte layout (version 6) with an 8-byte discriminator:
          </p>
          <div className="state-layout">
            <pre>{`Offset  Size  Type          Field
------  ----  ------------  ---------------------
  0       8   [u8; 8]       discriminator ("canpools")
  8      32   Pubkey        authority
 40      32   Pubkey        pending_authority
 72      32   Pubkey        treasury
104       8   u64 (LE)      total_cskr_supply
112       8   u64 (LE)      total_skr_managed
120      16   u128 (LE)     exchange_rate
136       2   u16 (LE)      fee_bps (deposit fee)
138       1   u8            bump
139       1   u8            is_initialized
140       8   u64 (LE)      total_skr_staked_guardian
148       8   u64 (LE)      pending_guardian_unstake
156       8   u64 (LE)      unstake_cooldown_end
164       2   u16 (LE)      target_reserve_bps
166       2   u16 (LE)      instant_unstake_fee_bps
168       4   u32 (LE)      total_pending_unstakes
172       8   u64 (LE)      total_escrowed_cskr
180       2   u16 (LE)      yield_commission_bps
182       2   u16 (LE)      cancel_fee_bps            [v6]
184       2   u16 (LE)      priority_surcharge_bps    [v6]
186       2   u16 (LE)      ultra_surcharge_bps       [v6]
188       2   u16 (LE)      swap_fee_bps              [v6]
                             --- total: 190 bytes ---`}</pre>
          </div>
          {liveState && (
            <div className="docs-callout info">
              <strong>Current on-chain values:</strong><br />
              Deposit fee: {liveState.depositFee} bps ({liveState.depositFee / 100}%) | 
              Instant fee: {liveState.instantFee} bps ({liveState.instantFee / 100}%) | 
              Yield commission: {liveState.yieldComm} bps ({liveState.yieldComm / 100}%) | 
              Ratio: {liveState.ratio.toFixed(6)}
            </div>
          )}

          {/* ── EXCHANGE RATE ────────────────────────────────────── */}
          <h2 id="exchange-rate">Exchange Rate</h2>
          <p>The cSKR:SKR exchange rate is determined by the pool's internal ratio:</p>
          <pre><code>{`ratio = total_skr_deposited / total_cskr_supply

// Deposit: user gets fewer cSKR as ratio increases
cskr_received = skr_deposited / ratio

// Withdraw: user gets more SKR as ratio increases
skr_received = cskr_burned * ratio`}</code></pre>
          <p>
            The ratio <strong>only increases</strong> - it can never decrease because:
          </p>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: 20, lineHeight: 1.8 }}>
            <li>Deposits: both numerator and denominator increase proportionally (ratio unchanged)</li>
            <li>Withdrawals: both decrease proportionally (ratio unchanged)</li>
            <li>Rewards: numerator increases, denominator unchanged (ratio increases)</li>
            <li>Fees: taken from the vault, sent to treasury (doesn't affect ratio)</li>
          </ul>

          {/* ── DEPOSIT ──────────────────────────────────────────── */}
          <h2 id="ix-deposit">Deposit (Tag 1)</h2>
          <div className="instruction-card">
            <div className="ix-header">
              <span className="ix-tag deposit">TAG 1</span>
              <span className="ix-name">Deposit</span>
            </div>
            <p className="ix-desc">
              Transfer SKR from the user's ATA into the pool vault. Mint proportional cSKR to the user's cSKR ATA.
              A deposit fee (currently {liveState?.depositFee ?? 0} bps) is deducted from the minted cSKR.
            </p>
            <div className="accounts-label">Instruction Data</div>
            <pre><code>{`[1] [amount: u64 LE]   // 9 bytes total
 │        │
 │        └── Amount of SKR to deposit (in lamports, 6 decimals)
 └────────── Tag byte = 1 (Deposit)`}</code></pre>
            <div className="accounts-label">Accounts</div>
            <table>
              <thead><tr><th>#</th><th>Account</th><th>Flags</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td>0</td><td>user</td><td><span className="badge-signer">signer</span> <span className="badge-writable">writable</span></td><td>Fee payer and token owner</td></tr>
                <tr><td>1</td><td>pool_state</td><td><span className="badge-writable">writable</span></td><td>Pool state PDA</td></tr>
                <tr><td>2</td><td>user_skr_ata</td><td><span className="badge-writable">writable</span></td><td>User's SKR token account</td></tr>
                <tr><td>3</td><td>pool_vault</td><td><span className="badge-writable">writable</span></td><td>Pool's SKR vault</td></tr>
                <tr><td>4</td><td>cskr_mint</td><td><span className="badge-writable">writable</span></td><td>cSKR mint account</td></tr>
                <tr><td>5</td><td>user_cskr_ata</td><td><span className="badge-writable">writable</span></td><td>User's cSKR token account</td></tr>
                <tr><td>6</td><td>mint_authority</td><td></td><td>Mint authority PDA</td></tr>
                <tr><td>7</td><td>treasury_skr_ata</td><td><span className="badge-writable">writable</span></td><td>Treasury's SKR ATA (fee recipient)</td></tr>
                <tr><td>8</td><td>token_program</td><td></td><td>SPL Token program</td></tr>
              </tbody>
            </table>
          </div>

          {/* ── WITHDRAW ─────────────────────────────────────────── */}
          <h2 id="ix-withdraw">Withdraw (Tag 2)</h2>
          <div className="instruction-card">
            <div className="ix-header">
              <span className="ix-tag withdraw">TAG 2</span>
              <span className="ix-name">Withdraw</span>
            </div>
            <p className="ix-desc">
              Burn cSKR from the user's ATA and transfer proportional SKR from the pool vault to the user.
              SKR received = cSKR burned × ratio.
            </p>
            <div className="accounts-label">Instruction Data</div>
            <pre><code>{`[2] [amount: u64 LE]   // 9 bytes total - amount of cSKR to burn`}</code></pre>
            <div className="accounts-label">Accounts</div>
            <table>
              <thead><tr><th>#</th><th>Account</th><th>Flags</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td>0</td><td>user</td><td><span className="badge-signer">signer</span> <span className="badge-writable">writable</span></td><td>Fee payer and token owner</td></tr>
                <tr><td>1</td><td>pool_state</td><td><span className="badge-writable">writable</span></td><td>Pool state PDA</td></tr>
                <tr><td>2</td><td>user_cskr_ata</td><td><span className="badge-writable">writable</span></td><td>User's cSKR token account</td></tr>
                <tr><td>3</td><td>cskr_mint</td><td><span className="badge-writable">writable</span></td><td>cSKR mint (for burn)</td></tr>
                <tr><td>4</td><td>pool_vault</td><td><span className="badge-writable">writable</span></td><td>Pool's SKR vault</td></tr>
                <tr><td>5</td><td>user_skr_ata</td><td><span className="badge-writable">writable</span></td><td>User's SKR token account</td></tr>
                <tr><td>6</td><td>mint_authority</td><td></td><td>Mint authority PDA (vault signer)</td></tr>
                <tr><td>7</td><td>token_program</td><td></td><td>SPL Token program</td></tr>
              </tbody>
            </table>
          </div>

          {/* ── INSTANT UNSTAKE ───────────────────────────────────── */}
          <h2 id="ix-instant">Instant Unstake (Tag 13)</h2>
          <div className="instruction-card">
            <div className="ix-header">
              <span className="ix-tag withdraw">TAG 13</span>
              <span className="ix-name">RequestInstantUnstake</span>
            </div>
            <p className="ix-desc">
              Transfer cSKR to the queue escrow, create an unstake request PDA, and receive SKR minus the instant fee.
              The fee portion is sent to the treasury's SKR ATA.
              Fee: {liveState?.instantFee ?? 300} bps = {liveState ? liveState.instantFee / 100 : 3}%.
              Users receive: <code>(cSKR x ratio) x (1 - fee/10000)</code>.
            </p>
            <div className="accounts-label">Instruction Data</div>
            <pre><code>{`[13] [cskr_amount: u64 LE] [tip_bps: u16 LE] [tier: u8]   // 12 bytes total
  |        |                    |                |   
  |        |                    |                +-- Queue tier (0 = standard)
  |        |                    +--- Tip in basis points (priority fee for faster processing)
  |        +--- Amount of cSKR to unstake (in lamports, 6 decimals)
  +---------- Tag byte = 13 (RequestInstantUnstake)`}</code></pre>
            <div className="accounts-label">Accounts</div>
            <table>
              <thead><tr><th>#</th><th>Account</th><th>Flags</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td>0</td><td>user</td><td><span className="badge-signer">signer</span> <span className="badge-writable">writable</span></td><td>Fee payer</td></tr>
                <tr><td>1</td><td>pool_state</td><td><span className="badge-writable">writable</span></td><td>Pool state PDA</td></tr>
                <tr><td>2</td><td>user_cskr_ata</td><td><span className="badge-writable">writable</span></td><td>User's cSKR (transferred to escrow)</td></tr>
                <tr><td>3</td><td>queue_escrow</td><td><span className="badge-writable">writable</span></td><td>Queue escrow PDA (holds cSKR)</td></tr>
                <tr><td>4</td><td>unstake_request</td><td><span className="badge-writable">writable</span></td><td>Per-user unstake request PDA</td></tr>
                <tr><td>5</td><td>user_skr_ata</td><td><span className="badge-writable">writable</span></td><td>User receives SKR (minus fee)</td></tr>
                <tr><td>6</td><td>mint_authority</td><td></td><td>Vault signer PDA</td></tr>
                <tr><td>7</td><td>token_program</td><td></td><td>SPL Token program</td></tr>
                <tr><td>8</td><td>system_program</td><td></td><td>System program (for PDA creation)</td></tr>
              </tbody>
            </table>
          </div>

          {/* ── ADMIN ────────────────────────────────────────────── */}
          <h2 id="ix-admin">Admin Instructions</h2>
          <p>
            These instructions require the pool authority as signer. They cannot be called by regular users.
          </p>
          <table>
            <thead><tr><th>Tag</th><th>Name</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>0</code></td><td>InitPool</td><td>Initialize pool state, create vault and cSKR mint. One-time.</td></tr>
              <tr><td><code>3</code></td><td>UpdateFee</td><td>Set deposit fee (bps). Authority only.</td></tr>
              <tr><td><code>4</code></td><td>UpdateTreasury</td><td>Change treasury pubkey. Authority only.</td></tr>
              <tr><td><code>5</code></td><td>TransferAuthority</td><td>Propose new authority. Requires acceptance.</td></tr>
              <tr><td><code>6</code></td><td>AcceptAuthority</td><td>Accept authority transfer. New authority signs.</td></tr>
              <tr><td><code>12</code></td><td>MigrateState</td><td>Migrate state layout to new version. Authority only.</td></tr>
              <tr><td><code>14</code></td><td>CancelInstantUnstake</td><td>Cancel a queued instant unstake request. User or authority.</td></tr>
              <tr><td><code>15</code></td><td>SetInstantFee</td><td>Set instant unstake fee (bps). Authority only.</td></tr>
              <tr><td><code>16</code></td><td>InitQueueEscrow</td><td>Initialize the queue escrow PDA. One-time.</td></tr>
              <tr><td><code>17</code></td><td>SetYieldCommission</td><td>Set yield commission (bps). Authority only.</td></tr>
            </tbody>
          </table>

          {/* ── GUARDIAN ──────────────────────────────────────────── */}
          <h2 id="ix-guardian">Guardian Staking</h2>
          <p>
            Guardian staking allows the pool authority to delegate staked SKR to external validators
            or yield sources. The pool vault's SKR is moved to a guardian and rewards are returned.
          </p>
          <table>
            <thead><tr><th>Tag</th><th>Name</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>8</code></td><td>StakeToGuardian</td><td>Transfer SKR from vault to guardian account</td></tr>
              <tr><td><code>9</code></td><td>UnstakeFromGuardian</td><td>Return SKR from guardian to vault</td></tr>
              <tr><td><code>10</code></td><td>WithdrawFromGuardian</td><td>Withdraw rewards from guardian</td></tr>
            </tbody>
          </table>

          {/* ── METADATA ─────────────────────────────────────────── */}
          <h2 id="ix-metadata">cSKR Metadata (Tag 18)</h2>
          <div className="instruction-card">
            <div className="ix-header">
              <span className="ix-tag meta">TAG 18</span>
              <span className="ix-name">CreateCskrMetadata</span>
            </div>
            <p className="ix-desc">
              Creates Metaplex token metadata for the cSKR mint. Called once by the authority.
              The metadata PDA is <ExplorerLink addr={ADDRESSES.metadata} />.
            </p>
            <div className="accounts-label">Instruction Data</div>
            <pre><code>{`[18] [name_len: u8] [name: utf8] [symbol_len: u8] [symbol: utf8] [uri_len: u8] [uri: utf8]

Example: tag=18, name="Canary Staked SKR", symbol="cSKR", uri=""`}</code></pre>
            <div className="accounts-label">Current Metadata</div>
            <table>
              <thead><tr><th>Field</th><th>Value</th></tr></thead>
              <tbody>
                <tr><td>Name</td><td>Canary Staked SKR</td></tr>
                <tr><td>Symbol</td><td>cSKR</td></tr>
                <tr><td>URI</td><td><em>(empty - token image via off-chain metadata)</em></td></tr>
              </tbody>
            </table>
          </div>

          {/* ── KOTLIN SDK ────────────────────────────────────────── */}
          <h2 id="sdk-kotlin">Kotlin / Android Integration</h2>
          <p>
            The Canary wallet and Perch apps use <strong>Artemis SDK 2.1.1</strong> for Solana
            transaction building. Three files form the integration layer:
          </p>
          <h3>CanaryPoolConstants.kt</h3>
          <pre><code>{`object CanaryPoolConstants {
    const val PROGRAM_ID = "CSkRjy7gyhWw6izU9jrJUKdwgzCCNM4m8edA4REbjQti"
    const val SKR_MINT   = "SKRbvo6Gf7GondiT3BbTfuRDPqLWei4j2Qy2NPGZhW3"
    const val CSKR_MINT  = "GT6LYQN3fnwLm127ULvYVg5xDvmacfUEah6KQQYaK7Jx"
    // ... full PDAs and instruction tags
}`}</code></pre>
          <h3>CanaryPoolInstructions.kt</h3>
          <pre><code>{`// Deposit example (9 accounts, matches processor.rs)
fun buildDeposit(userPubkey: Pubkey, amount: Long): TransactionInstruction {
    val data = ByteArray(9)
    data[0] = TAG_DEPOSIT.toByte()
    ByteBuffer.wrap(data, 1, 8)
        .order(ByteOrder.LITTLE_ENDIAN)
        .putLong(amount)
    
    return TransactionInstruction(
        Pubkey.fromBase58(PROGRAM_ID),
        listOf(
            AccountMeta(userPubkey, isSigner = true, isWritable = true),
            AccountMeta(POOL_STATE_PDA, isWritable = true),
            AccountMeta(userSkrAta, isWritable = true),
            AccountMeta(POOL_VAULT, isWritable = true),
            AccountMeta(CSKR_MINT_PUBKEY, isWritable = true),
            AccountMeta(userCskrAta, isWritable = true),
            AccountMeta(MINT_AUTHORITY_PUBKEY),
            AccountMeta(TREASURY_SKR_ATA, isWritable = true),
            AccountMeta(TOKEN_PROGRAM_PUBKEY),
        ),
        data,
    )
}`}</code></pre>
          <h3>CanaryPoolState.kt</h3>
          <pre><code>{`data class PoolStateV6(
    val authority: String,
    val pendingAuthority: String,
    val treasury: String,
    val totalCskrSupply: Long,
    val totalSkrManaged: Long,
    val depositFeeBps: Int,
    val instantFeeBps: Int,
    val yieldCommissionBps: Int,
) {
    val ratio: Double get() =
        if (totalCskrSupply > 0) totalSkrManaged.toDouble() / totalCskrSupply
        else 1.0
}`}</code></pre>

          {/* ── WEB SDK ───────────────────────────────────────────── */}
          <h2 id="sdk-web">Web / JavaScript Integration</h2>
          <p>
            The staking dashboard uses <code>@solana/web3.js</code> and Solana Wallet Adapter.
            All instruction building is done client-side with the constants matching the Kotlin layer.
          </p>
          <pre><code>{`import { PublicKey, TransactionInstruction } from '@solana/web3.js'

const PROGRAM_ID  = new PublicKey('CSkRjy7gyhWw6izU9jrJUKdwgzCCNM4m8edA4REbjQti')
const POOL_STATE  = new PublicKey('5mj13zoqBwE61ELA6QjRNjcHDf5bvms62Z48dtvgGXCM')
const POOL_VAULT  = new PublicKey('28jV6eBH8rsRGeGoaVWcxFvkobfKHH8797nRSLQHTsQt')
const CSKR_MINT   = new PublicKey('GT6LYQN3fnwLm127ULvYVg5xDvmacfUEah6KQQYaK7Jx')
const MINT_AUTH   = new PublicKey('G2HoxRjhjeX7rHGH6eay866gpxJTLbKsvChpaXbGsfoF')
const TREASURY_ATA = new PublicKey('6Aq428L6M7LBTGbqdq8GbNVXi5YCXFRBKVtgnJiXmoYw')

function buildDeposit(userPubkey, amountLamports) {
  const data = Buffer.alloc(9)
  data[0] = 1  // TAG_DEPOSIT
  data.writeBigUInt64LE(BigInt(amountLamports), 1)
  
  return new TransactionInstruction({
    programId: PROGRAM_ID,
    keys: [
      { pubkey: userPubkey, isSigner: true, isWritable: true },
      { pubkey: POOL_STATE, isSigner: false, isWritable: true },
      { pubkey: userSkrAta, isSigner: false, isWritable: true },
      { pubkey: POOL_VAULT, isSigner: false, isWritable: true },
      { pubkey: CSKR_MINT, isSigner: false, isWritable: true },
      { pubkey: userCskrAta, isSigner: false, isWritable: true },
      { pubkey: MINT_AUTH, isSigner: false, isWritable: false },
      { pubkey: TREASURY_ATA, isSigner: false, isWritable: true },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  })
}`}</code></pre>

          {/* ── FEES ──────────────────────────────────────────────── */}
          <h2 id="fees">Fee Structure</h2>
          <table>
            <thead><tr><th>Fee</th><th>Current</th><th>Range</th><th>Collected By</th></tr></thead>
            <tbody>
              <tr><td>Deposit Fee</td><td>{liveState?.depositFee ?? 0} bps (0%)</td><td>0 – 10,000 bps</td><td>Deducted from minted cSKR</td></tr>
              <tr><td>Instant Unstake Fee</td><td>{liveState?.instantFee ?? 300} bps (3%)</td><td>0 – 10,000 bps</td><td>Sent to treasury SKR ATA</td></tr>
              <tr><td>Yield Commission</td><td>{liveState?.yieldComm ?? 800} bps (8%)</td><td>0 – 10,000 bps</td><td>Deducted from guardian rewards</td></tr>
            </tbody>
          </table>
          <div className="docs-callout warn">
            <strong>Note:</strong> All fee parameters are adjustable by the pool authority. The deposit fee
            is currently set to 0% to encourage early adoption.
          </div>

          {/* ── SECURITY ─────────────────────────────────────────── */}
          <h2 id="security">Security Model</h2>
          <h3>Access Control</h3>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: 20, lineHeight: 1.8 }}>
            <li>Pool authority (signer check) required for all admin operations</li>
            <li>Two-step authority transfer (propose + accept) prevents accidental loss</li>
            <li>Mint authority is a PDA - only the program can mint/burn cSKR</li>
            <li>Vault signer is a PDA - only the program can move SKR from the vault</li>
          </ul>
          <h3>Economic Security</h3>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: 20, lineHeight: 1.8 }}>
            <li>Exchange rate can only increase (never decrease), protecting stakers</li>
            <li>Deposit fee prevents sandwich attacks on the pool</li>
            <li>Instant unstake fee disincentivizes short-term speculation</li>
            <li>Guardian staking is authority-only - users cannot extract vault funds directly</li>
          </ul>
          <h3>Build Verification</h3>
          <pre><code>{`# Binary hash (SHA-256)
81573aae8495ad4622fc73a8f284bf90b19aa7f83a6eeb6e64f56d4ff3bab9ac

# Verify with solana-verify
solana-verify get-executable-hash CSkRjy7gyhWw6izU9jrJUKdwgzCCNM4m8edA4REbjQti

# Build from source
cargo build-sbf --manifest-path programs/canary_skr_pool/Cargo.toml`}</code></pre>

          <div className="docs-callout info">
            <strong>Framework:</strong> Pinocchio v0.10.2 + pinocchio-token v0.5.0.
            No <code>solana-program</code> runtime dependency - minimal attack surface, 
            small binary (113 KB).
          </div>
        </main>
      </div>
    </div>
  )
}
